/**
 * @fileoverview AI Operations service — inherently-online data access for the
 *   dedicated AI Operations dashboard (job execution health, AI dependency
 *   telemetry, AI Foundry health, token-usage cost indicators, embedding progress).
 * @module system-web/services/aiOperationsService
 *
 * Responsibilities:
 * - Provide typed wrappers over the read-only /api/system/ai-operations routes.
 * - Own the (recognized) online-only API access for live monitoring data.
 *
 * Dependencies:
 * - apiClient.apiFetch (bearer-token attach + 401 refresh).
 *
 * Architecture:
 * - Local-first EXCEPTION: this is live operational monitoring data, not user
 *   data — the same inherently-online bypass already used by systemStatusService
 *   and mongoDbMonitoringService. Dexie is intentionally NOT the source of truth
 *   here; data is fetched on demand and never persisted. Hooks call these
 *   functions; components never call the API directly.
 *
 * Notes:
 * - Responses never contain Azure OpenAI keys, connection strings, or credentials.
 *
 * @localFirst
 * Source of Truth: server (live, non-persisted monitoring data).
 * Write Flow:      none — read-only.
 * Sync Strategy:   on-demand fetch per dashboard load / refresh; no Dexie caching.
 * Conflict Resolution: n/a (read-only).
 */

import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// Types — mirror the backend aiOperationsService DTOs
// ---------------------------------------------------------------------------

export interface AiMetricPoint {
  t: string;
  v: number;
}

/**
 * @contract AiJobTypeMetric
 * Purpose: Per-job-type execution summary for one AI Container App Job type.
 * Fields:  jobType, started, completed, failed, successRatePct, avgDurationMs,
 *          avgRetryCount.
 * Rules:   null durations / retries mean no completed / failed runs in the window.
 * Sync:    n/a (live monitoring data).
 */
export interface AiJobTypeMetric {
  jobType: string;
  started: number;
  completed: number;
  failed: number;
  successRatePct: number;
  avgDurationMs: number | null;
  avgRetryCount: number | null;
}

export interface AiJobMetrics {
  windowHours: number;
  jobs: AiJobTypeMetric[];
  totalRuns: number;
  overallFailureRatePct: number;
}

export interface AiDependencyHealth {
  windowHours: number;
  calls: number;
  failed: number;
  failureRatePct: number;
  avgDurationMs: number;
  throttleCount: number;
  series: AiMetricPoint[];
}

export interface AiFoundryHealth {
  endpointHost: string | null;
  callCount: number;
  failedCount: number;
  avgLatencyMs: number | null;
  throttleCount: number;
  available: boolean;
}

export interface AiTokenUsagePoint {
  t: string;
  promptTokens: number;
  generatedTokens: number;
}

export interface AiTokenUsage {
  windowHours: number;
  promptTokens: number;
  generatedTokens: number;
  totalTokens: number;
  series: AiTokenUsagePoint[];
}

export interface AiEmbeddingJobProgress {
  jobType: string;
  completed: number;
  failed: number;
  lastRunAt: string | null;
  avgDurationMs: number | null;
}

export interface AiEmbeddingProgress {
  windowHours: number;
  jobs: AiEmbeddingJobProgress[];
}

/**
 * @contract AiOperationsOverview
 * Purpose: One-shot snapshot backing the AI Operations dashboard.
 * Fields:  configured, windowHours, jobMetrics, dependencies, foundry, tokenUsage,
 *          embedding, collectedAt.
 * Rules:   `configured` reflects whether Log Analytics is wired; each section is
 *          null when its source is unconfigured / empty.
 * Sync:    n/a (live monitoring data).
 */
export interface AiOperationsOverview {
  configured: boolean;
  windowHours: number;
  jobMetrics: AiJobMetrics | null;
  dependencies: AiDependencyHealth | null;
  foundry: AiFoundryHealth | null;
  tokenUsage: AiTokenUsage | null;
  embedding: AiEmbeddingProgress | null;
  collectedAt: string;
}

// ---------------------------------------------------------------------------
// Service functions — real-time data; valid Dexie bypass (inherently online)
// ---------------------------------------------------------------------------

export async function getAiOperationsOverview(windowHours = 24): Promise<AiOperationsOverview> {
  return apiFetch(
    `/api/system/ai-operations/overview?windowHours=${encodeURIComponent(String(windowHours))}`,
  ) as Promise<AiOperationsOverview>;
}
